import type { DeckSource } from '../../sync-engine/schema/shared-types';
import { sortDeckMaterials } from './order';
import { encodeMaterialRef } from './ref-codec';
import type { DeckMaterialItem, DeckMaterialKind, DeckMaterialLabels } from './types';

/**
 * Sources that carry nothing a viewer can show. Same rule as the panes'
 * `hasReadableSource`: `MANUAL` / `GPT` / `APKG` decks have no document.
 */
const NON_READABLE_KINDS: ReadonlySet<DeckSource['kind']> = new Set([
  'manual',
  'gpt',
  'apkg',
]);

/** Legacy `sourceLabel` values that are placeholders, not names. */
const PLACEHOLDER_LABELS: ReadonlySet<string> = new Set(['MANUAL', 'GPT', 'APKG']);

export function isReadableSource(source: DeckSource): boolean {
  if (source.pdfInfo?.filePath) return true;
  if (NON_READABLE_KINDS.has(source.kind)) return false;
  return !!source.sourceLabel?.trim();
}

/**
 * Display kind of a source. `origin` wins when present; a video is also known
 * by the `pdfInfo.video` its creation stored (the only trace on a deck made
 * before `origin` existed). Other legacy entries fall back on `kind`.
 */
export function materialKindOfSource(source: DeckSource): DeckMaterialKind {
  if (source.origin === 'youtube' || source.link || source.pdfInfo?.video)
    return 'youtube';
  if (source.origin === 'photos' || source.kind === 'photo') return 'photos';
  if (source.origin === 'audio' || source.kind === 'transcription') return 'audio';
  if (source.origin === 'text' || source.kind === 'text') return 'text';
  return 'document';
}

/**
 * A source's display name — THE ONE place it is decided. The name the user
 * gave it (⋯ → Rename, stored as `title`) wins outright; otherwise its link
 * title, then its stored label unless that is a placeholder or the pasted
 * text itself, else a numbered fallback. `index` is the source's position
 * among the deck's READABLE sources.
 *
 * Every surface that names a source — the tab bar, the grid tile, the
 * generation / quiz / oral-exam / case-study pickers, the badge a card wears
 * — reads it through here (directly or via `listDeckMaterials`), which is
 * what makes a rename show up on all of them at once, on both platforms.
 *
 * THE NUMBER IS A POSITION, NOT A NAME. It is recomputed on every render, so
 * it moves when a material is added, removed, merged in from another deck, or
 * stops being readable — delete the second of three and the third becomes the
 * second. That is deliberate: the alternative, a stored ordinal, is a second
 * identity the user cannot see has gone stale, and it would still move on
 * merge. Nothing may address a material by its number; the URL, the
 * remembered tab and every card ref address it by id.
 */
export function sourceDisplayLabel(
  source: DeckSource,
  index: number,
  labels: Pick<DeckMaterialLabels, 'source' | 'text'>,
): string {
  const own = sourceOwnName(source);
  if (own) return own;
  if (materialKindOfSource(source) === 'text') return `${labels.text} ${index + 1}`;
  return `${labels.source} ${index + 1}`;
}

/**
 * The name a source carries ITSELF — the user's rename, its link title, or
 * its stored label — without the numbered fallback, which is a position and
 * never a name. Undefined when it has none. {@link sourceDisplayLabel} is this
 * plus the fallback.
 */
export function sourceOwnName(source: DeckSource): string | undefined {
  const title = source.title?.trim();
  if (title) return title;
  const linkTitle = source.link?.title?.trim();
  if (linkTitle) return linkTitle;
  // Pasted text's label is the text itself, not a name.
  if (materialKindOfSource(source) === 'text') return undefined;
  const label = source.sourceLabel?.trim();
  if (label && !PLACEHOLDER_LABELS.has(label)) return label;
  return undefined;
}

export interface NoteMaterialInput {
  noteId: string;
  title?: string;
  sortKey?: string;
}

export interface MindmapMaterialInput {
  mindmapId: string;
  title?: string;
  sortKey?: string;
}

export interface ListDeckMaterialsInput {
  sources?: readonly DeckSource[];
  notes: readonly NoteMaterialInput[];
  mindmaps: readonly MindmapMaterialInput[];
}

/**
 * The pane's items in display order — the deck's MATERIALS ORDER (`order.ts`):
 * entries the user has placed by dragging a tab or a tile, by their key;
 * then, in stored order, everything never placed — readable sources (index 0
 * is the primary), notes, mind maps. A deck nobody has reordered reads
 * exactly as that stored order. Pure — the caller decides what a note or map
 * input is.
 *
 * A source's numbered fallback name ("Source 2") counts its STORED position,
 * not its drawn one, so dragging "Source 2" ahead of "Source 1" visibly moves
 * it rather than silently swapping the two names.
 */
export function listDeckMaterials(
  input: ListDeckMaterialsInput,
  labels: DeckMaterialLabels,
): DeckMaterialItem[] {
  const items: DeckMaterialItem[] = [];

  const readable = (input.sources ?? []).filter(isReadableSource);
  readable.forEach((source, index) => {
    const ref = { type: 'source' as const, sourceId: source.sourceId };
    items.push({
      ref,
      key: encodeMaterialRef(ref),
      kind: materialKindOfSource(source),
      label: sourceDisplayLabel(source, index, labels),
      addedAtISO: source.addedAtISO,
      ...(source.sortKey !== undefined ? { sortKey: source.sortKey } : {}),
    });
  });

  input.notes.forEach((note, index) => {
    const ref = { type: 'note' as const, noteId: note.noteId };
    items.push({
      ref,
      key: encodeMaterialRef(ref),
      kind: 'note',
      label: note.title?.trim() || `${labels.note} ${index + 1}`,
      ...(note.sortKey !== undefined ? { sortKey: note.sortKey } : {}),
    });
  });

  input.mindmaps.forEach((map, index) => {
    const ref = { type: 'mindmap' as const, mindmapId: map.mindmapId };
    items.push({
      ref,
      key: encodeMaterialRef(ref),
      kind: 'mindmap',
      label: map.title?.trim() || `${labels.mindmap} ${index + 1}`,
      ...(map.sortKey !== undefined ? { sortKey: map.sortKey } : {}),
    });
  });

  return sortDeckMaterials(items);
}

/** Ellipsize a segment label so a long filename cannot stretch the bar. */
export function truncateMaterialLabel(label: string, max = 16): string {
  const trimmed = label.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, Math.max(1, max - 1)).trimEnd()}…`;
}
